import { MUICheckBox } from "@abb/arcadia-ui-react"
import { useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm, useWatch } from "react-hook-form"
import { Navigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { digitalManagerApi } from "../../../../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../../../../components/DiMaDetailsContent"
import { useApproverTypeSelectRenderer } from "../../../../../../../components/Helpers"
import {
  ApplicationRoleApproverGet,
  ApplicationRoleApproverPut,
  ApplicationRoleApproverTypeEnum,
} from "../../../../../../../models/ApplicationRoleApprover"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../../../../models/Authorization"
import { UserInfo } from "../../../../../../../models/User"

export function ApplicationRoleApproverPropertyDetails(props: {
  readonly approver: ApplicationRoleApproverGet
  readonly user: UserInfo
  readonly setApprover: (updateApprover: ApplicationRoleApproverGet) => void
}) {
  const { approver, user, setApprover } = props
  const { applicationId, roleId, userId, propertyName, propertyValue } = useParams()
  const returnURL = `/applications/${applicationId}/roles/${roleId}/approvers/${userId}`

  const formMethods = useForm<ApplicationRoleApproverPut>()

  const { handleSubmit, reset, control, setValue } = formMethods

  useEffect(() => {
    reset({
      type: approver.properties[0].approverType,
      isAutomatic: approver.properties[0].isAutomatic,
    })
  }, [approver.properties, reset])

  const onSubmit = useCallback(
    (values: ApplicationRoleApproverPut) =>
      digitalManagerApi
        .put<ApplicationRoleApproverGet>(
          `/applications/${applicationId}/roles/${roleId}/approvers/${userId}/properties/${propertyName}/values/${propertyValue}`,
          {
            type: values.type,
            isAutomatic: values.isAutomatic,
          }
        )
        .then((res) => {
          toast.success("Role approver rule updated")
          setApprover(res.data)
        })
        .catch(() => {
          toast.error("Cannot update role approver rule")
          throw new Error("Cannot update role approver rule")
        }),
    [applicationId, roleId, userId, propertyName, propertyValue, setApprover]
  )

  const selectedType = useWatch({
    control,
    name: "type",
  })

  useEffect(() => {
    if (
      selectedType !== ApplicationRoleApproverTypeEnum.Approver &&
      selectedType !== ApplicationRoleApproverTypeEnum.BackupApprover
    ) {
      setValue("isAutomatic", false)
    }
  }, [selectedType, setValue])

  const renderTypeSelect = useApproverTypeSelectRenderer({
    valueExtractor: (first: unknown) =>
      typeof first === "string" ? first : (first as { id?: string })?.id ?? "",
  })

  const handleIsAutomaticToggle = useCallback(
    (field: ControllerRenderProps<ApplicationRoleApproverPut, "isAutomatic">) =>
      (e: React.MouseEvent) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderIsAutomaticCheckbox = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<ApplicationRoleApproverPut, "isAutomatic">
    }) => (
      <MUICheckBox
        {...field}
        id="isAutomatic"
        checked={Boolean(field.value)}
        handleToggle={handleIsAutomaticToggle(field)}
        disabled={
          selectedType !== ApplicationRoleApproverTypeEnum.Approver &&
          selectedType !== ApplicationRoleApproverTypeEnum.BackupApprover
        }
      />
    ),
    [selectedType, handleIsAutomaticToggle]
  )

  if (!approver?.properties[0].approverType || !applicationId) {
    return <Navigate to={returnURL} />
  }

  const detailsContentProps: DiMaDetailsContentProps<ApplicationRoleApproverPut> = {
    formMethods,
    canWrite: hasUserAccessToApplicationFeature(
      user.authorizations.applications,
      applicationId,
      DimaFeatureShortNameEnum.AM,
      AccessLevel.Write
    ),
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Property Name",
        displayItem: <div className="title">{propertyName}</div>,
      },
      {
        id: 1,
        label: "Property Value",
        displayItem: <div className="title">{approver.properties[0].label}</div>,
      },
      {
        id: 2,
        label: "Approver Type",
        displayItem: <div className="title">{approver.properties[0].approverType}</div>,
        editItem: (
          <Controller
            name="type"
            control={control}
            rules={{ required: "Type field is required" }}
            render={renderTypeSelect}
          />
        ),
      },
      {
        id: 3,
        label: "Is Automatic",
        displayItem: (
          <div className="title">
            {approver.properties[0].isAutomatic?.toString() || "false"}
          </div>
        ),
        editItem: (
          <Controller
            name="isAutomatic"
            control={control}
            render={renderIsAutomaticCheckbox}
          />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
